import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { LoadingService } from 'src/app/services/Loading/loading.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { MaskPipe } from 'ngx-mask';
import UIDefaultTheme from 'src/themes';
import Utils, { UtilsValidators, BrValidators } from 'src/app/helpers/utils';
import { GetHelper } from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-company',
  templateUrl: './dashboard-company.component.html',
  styleUrls: ['./dashboard-company.component.scss'],
})
export class DashboardCompanyComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  activeTab: 'departments' | 'users' = 'departments';
  tabs = [
    { label: 'Departamentos', value: 'departments' },
    { label: 'Usuários', value: 'users' },
  ];

  UIDefaultTheme = UIDefaultTheme;
  loading = false;
  
  // Dados da empresa vêm do tema
  companyData = {
    corporate_name: UIDefaultTheme.brand.company_name || UIDefaultTheme.brand.name,
    document_number: UIDefaultTheme.brand.company_cnpj || '-',
    status: 'active',
    statusDisplay: 'Ativa',
  };

  permissionObj = {
    canView: true,
    canAdd: true,
    canFilter: true,
    canInactive: true,
    canReset: true,
  };

  showUserModal = false;
  isMaster = false;
  isSimple = false;
  submiting = false;
  addingUser = false;
  loadingProfiles = false;
  documentExists = true; // Começa TRUE - só libera se API confirmar que NÃO existe
  checkingDocument = false;
  departmentOptions: SelectItem[] = [];
  profileOptions: SelectItem[] = [];
  groups: any[] = [];

  form = this.formBuilder.group({
    name: [null, [Validators.required, UtilsValidators.fullName]],
    department: [null, [Validators.required]],
    email: [null, [Validators.required, UtilsValidators.email]],
    profile: [null, []], // Não obrigatório mais para usuário comum
    document: [null, [Validators.required, Validators.maxLength(14)]],
  });

  constructor(
    public authService: AuthService,
    private api: ApiService,
    private router: Router,
    private toast: ToastService,
    private maskPipe: MaskPipe,
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private storageService: StorageService,
    private companyService: CompanyService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    this.getDepartments();
    this.getGroups();

    // Removido: não chama mais API ao selecionar departamento para usuário comum
    // this.form.controls.department.valueChanges
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe((value) => {
    //     this.form.controls.profile.setValue(null);
    //     if (value) {
    //       this.getProfiles();
    //     } else {
    //       this.profileOptions = [];
    //     }
    //   });

    // Observar mudanças no documento para verificar se existe
    this.form.controls.document.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500)
      )
      .subscribe(async (value) => {
        const documentClean = value ? value.replace(/[^\d]+/g, '') : '';
        
        // SEMPRE reseta para TRUE ao mudar documento - campos NUNCA aparecem antes da API
        this.documentExists = true;
        
        // Quando o documento tem tamanho válido (apenas CPF - 11 dígitos)
        if (documentClean.length === 11) {
          this.checkingDocument = true;
          
          // Validar apenas CPF (11 dígitos)
          if (documentClean.length !== 11) {
            this.checkingDocument = false;
            this.documentExists = true;
            return;
          }
          
          const isValid = BrValidators.cpf(documentClean);
          
          if (!isValid) {
            // Documento inválido - mantém TRUE (não mostra campos)
            this.checkingDocument = false;
            this.documentExists = true;
            return;
          }
          
          // Chamar API para verificar se documento existe
          try {
            const res = await this.api.post({
              route: 'validate-user-paper-exists/',
              body: { document: documentClean },
              token: true,
            });
            
            // Para o loading imediatamente ANTES de processar a resposta
            this.checkingDocument = false;
            
            // Verifica se o documento já existe OU se é um colaborador
            // Se for colaborador (master ou comum), também deve travar
            const shouldBlock = res.exists === true || res.is_collaborator === true;
            
            // Define documentExists baseado na resposta da API
            // Se exists OU is_collaborator, bloqueia os campos
            this.documentExists = shouldBlock;
            
            // Se o documento já existe OU é um colaborador, para tudo aqui e não mostra campos
            // O componente masked-input já vai mostrar o erro "Este documento já está cadastrado"
            if (shouldBlock) {
              // Documento já cadastrado ou é colaborador - trava aqui, não libera nada
              return;
            }
            
            // SÓ chega aqui se documentExists === FALSE E is_collaborator === FALSE (não existe no sistema e não é colaborador)
            // Nesse caso, os campos serão liberados pelo *ngIf="!documentExists && !checkingDocument"
          } catch (error) {
            console.error('Erro ao validar documento:', error);
            // Para o loading imediatamente em caso de erro
            this.checkingDocument = false;
            // Mantém documentExists = true para não mostrar campos em caso de erro
            this.documentExists = true;
          }
        } else {
          this.checkingDocument = false;
          this.documentExists = true; // Documento incompleto = não mostra campos
        }
      });

    this.companyService.departmentAdded
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getDepartments();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabChange(tab: string) {
    this.activeTab = tab as any;
  }

  formatCnpj(cnpj: string): string {
    if (!cnpj) return '-';
    return Utils.formatDocumentForDisplay(cnpj);
  }

  handleChangeValue(type: string) {
    if (type === 'master') {
      if (this.isMaster) {
        this.isMaster = false;
        this.isSimple = true;
      } else {
        this.isMaster = true;
        this.isSimple = false;
      }
    }

    if (type === 'simple') {
      if (this.isSimple) {
        this.isSimple = false;
        this.isMaster = true;
      } else {
        this.isSimple = true;
        this.isMaster = false;
      }
    }

    // Removido: perfil não é mais obrigatório para usuário comum
    // this.form.controls.profile.setValidators(
    //   this.isSimple ? [Validators.required] : []
    // );
    this.form.controls.profile.setValue(null);
    this.form.controls.profile.updateValueAndValidity();

    this.form.controls.department.setValidators(
      this.isMaster ? [] : [Validators.required]
    );
    this.form.controls.department.setValue(null);
    this.form.controls.department.updateValueAndValidity();

    // Removido: não busca mais perfis ao mudar tipo de usuário
    // if (!this.isMaster) {
    //   this.getProfiles();
    // } else {
    //   this.profileOptions = [];
    // }
  }

  async getDepartments() {
    try {
      const res = await this.api.get({
        route: 'department/',
        token: true,
      });

      this.departmentOptions = res.map((item) => ({
        label: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
  }

  async getProfiles() {
    // Se for master, não precisa buscar perfis
    if (this.isMaster) {
      this.profileOptions = [];
      return;
    }

    this.loadingProfiles = true;
    this.loadingService.show('Carregando perfis de acesso...');
    try {
      // Pega o company_id do usuário logado
      const companyId = this.authService.user?.active_register?.register?.company?.id;
      
      if (!companyId) {
        this.toast.show('error', 'Erro', 'ID da empresa não encontrado.');
        return;
      }

      const res = await this.api.get({
        route: `api/v3/companies/${companyId}/`,
        params: {
          tab: 'user_profile',
        },
        token: true,
      });

      const profiles = res.results || res;
      this.profileOptions = profiles.map((item) => ({
        label: item.description,
        value: item.id,
        extra: item.id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loadingProfiles = false;
      this.loadingService.hide();
    }
  }

  toggleUserModal() {
    // Se não estiver na aba de usuários, mudar para ela primeiro
    if (this.activeTab !== 'users') {
      this.activeTab = 'users';
    }
    
    this.showUserModal = !this.showUserModal;
    this.form.reset();
    this.documentExists = true; // Reseta para TRUE
    this.checkingDocument = false;

    if (this.showUserModal) {
      this.isSimple = true;
      this.isMaster = false;
      // Removido: perfil não é mais obrigatório
      // this.form.controls.profile.setValidators([Validators.required]);
      this.form.controls.department.setValidators([Validators.required]);
      this.form.controls.profile.updateValueAndValidity();
      this.form.controls.department.updateValueAndValidity();
    }
  }

  handleCloseModal() {
    this.showUserModal = false;
    this.form.reset();
    this.documentExists = true; // Reseta para TRUE
    this.checkingDocument = false;
    this.isSimple = true;
    this.isMaster = false;
    // Removido: perfil não é mais obrigatório
    // this.form.controls.profile.setValidators([Validators.required]);
    this.form.controls.department.setValidators([Validators.required]);
    this.form.controls.profile.updateValueAndValidity();
    this.form.controls.department.updateValueAndValidity();
  }

  async submit() {
    this.submiting = true;
    this.addingUser = true;
    try {
      const valuesHelper = new GetHelper(this.form.value);
      
      // Se for master, não precisa de companyId
      const companyId = this.isMaster 
        ? null 
        : this.authService.user?.active_register?.register?.company?.id;
      
      // Removido: validação de companyId que estava impedindo o envio
      // if (!this.isMaster && !companyId) {
      //   this.toast.show('error', 'Erro', 'ID da empresa não encontrado.');
      //   return;
      // }

      const last_name = valuesHelper.get('name').split(' ').slice(1).join(' ');
      const collaboratorPayload: any = {
        email: valuesHelper.get('email'),
        first_name: valuesHelper.get('name').split(' ')[0],
        last_name,
        username: Utils.onlyNumbers(valuesHelper.get('document')),
        is_approver: this.isMaster,
        company: companyId,
        department: this.isMaster ? null : valuesHelper.get('department'),
        type_dispay:
          valuesHelper.get('document').length === 14
            ? 'Pessoa Jurídica'
            : 'Pessoa Física',
      };

      // Removido: não envia mais perfil de acesso para usuário comum
      // if (!this.isMaster && valuesHelper.get('profile')) {
      //   collaboratorPayload.permission_profile = valuesHelper.get('profile');
      // }

      // Removido: não chama mais a API user_group_user
      // const payload = {
      //   username: Utils.onlyNumbers(valuesHelper.get('document')),
      //   simple_user: !this.isMaster,
      //   manager_user: this.isMaster,
      //   company: this.isMaster ? [] : [companyId],
      //   permission_group: this.getPermissionGroup(),
      //   profiles: [],
      //   department: this.isMaster ? [] : [valuesHelper.get('department')],
      // };

      // Chama apenas a API de collaborator
      await this.api.post({
        route: 'api/collaborator/',
        token: true,
        body: collaboratorPayload,
      });

      // Após sucesso (200), remove loading spinner e fecha modal
      this.addingUser = false;
      this.toast.show('info', 'Sucesso', 'Usuário cadastrado com sucesso.');
      this.showUserModal = false;
      this.form.reset();
      this.isSimple = true;
      this.isMaster = false;
      this.documentExists = true;
      this.checkingDocument = false;

      // Recarrega a lista de usuários se estiver na aba de usuários (mostra skeleton durante o get)
      if (this.activeTab === 'users') {
        this.companyService.emitUserAdded();
      }
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao salvar os dados.');
      this.addingUser = false;
    }
    this.submiting = false;
  }

  async getGroups() {
    try {
      const companyId = this.authService.user?.active_register?.register?.company?.id;
      
      // Se não tiver companyId, não busca grupos (pode ser master do sistema)
      if (!companyId) {
        this.groups = [];
        return;
      }

      const res = await this.api.get({
        route: `api/v3/companies/${companyId}/`,
        token: true,
        params: {
          tab: 'groups',
        },
      });

      this.groups = res.results || [];
    } catch (error) {
      console.error(error);
      this.groups = [];
    }
  }

  getPermissionGroup() {
    const departmentLabel = this.departmentOptions.find(
      (item) => item.value === this.form.value.department
    )?.label;
    const group = this.groups.find(
      (item) => item.department === departmentLabel
    );
    return group ? [group.id] : [];
  }

  getDisabled() {
    return this.form.invalid || this.submiting;
  }
}

